import * as React from 'react';
import * as _ from 'underscore';

import { connect } from 'react-redux';
import { Messages } from '../../codegen/Localize';
import { angularProviderHOC } from '../../common/react/AngularProviderHOC';
import { DeprecatedReactMenu } from '../../common/react/components/Menu';
import { HelpActions } from '../../common/react/HelpActions';
import { PageHeaderButtonClickEvent, TelemetryEvents } from '../../common/telemetryEvents/TelemetryEvents';
import { imgPath } from '../../common/utilities/ImgUrl';
import { ServerService } from '../../ts/services/ServerService';
import { Telemetry } from '../../commonInfra/services/Telemetry';
import { PageHeaderButtonType, PageHeaderIcons } from '../layout/PageHeader';
import {
  CompositeSessionInfoSelectors,
  SessionServerSelectors
} from '../selectors/SessionInfoSelectors';
import { ServiceLocator } from '../ServiceLocator';
import { AppState } from '../state/AppState';
import { MapStateToProps } from '../utils/ConnectUtils';
import { HeaderMenuButton } from './HeaderMenuButton';
import { HelpMenuPanel } from './HelpMenuPanel';
import { HelpService } from '../../commonInfra/services/HelpService';

export interface HelpMenuExternalProps {
  readonly style?: any;
  readonly buttonType: PageHeaderButtonType;
}

export interface HelpMenuPropsFromRedux {
  readonly isOfflineHelpEnabled: boolean;
  readonly isSessionSiteOrServerAdmin: boolean;
}

export interface HelpMenuPropsFromAngular {
  readonly helpActions: HelpActions;
  readonly serverHelpLabel: string;
  readonly serverHelpUrl: string;
  readonly serverName: string;
  readonly supportUrl: string;
  readonly whatIsNewUrl: string;
}

export type HelpMenuViewComponentProps = HelpMenuExternalProps & HelpMenuPropsFromRedux & HelpMenuPropsFromAngular;

const helpIcons: PageHeaderIcons = {
  lightIcons: {
    active: imgPath('spaces_toolbar_help_active_white.svg'),
    inactive: imgPath('spaces_toolbar_help_white.svg')
  },
  regularIcons: {
    active: imgPath('spaces_toolbar_help_active.svg'),
    inactive: imgPath('spaces_toolbar_help.svg')
  }
};

export class HelpMenuViewComponent extends React.Component<HelpMenuViewComponentProps> {

  private itemSelected = () => {
    const ref = this.refs['menu'] as DeprecatedReactMenu;
    if (ref) {
      ref.closeMenu();
    }
    window.removeEventListener('blur', this.onWindowBlur);
  }

  public render(): JSX.Element {
    const icons = (this.props.buttonType === 'light') ? helpIcons.lightIcons : helpIcons.regularIcons;

    return DeprecatedReactMenu.element({
      ref: 'menu',
      tbTestId: 'help-menu',
      style: this.props.style,
      alignPanelRight: true,
      focusOnEscape: true,

      menuButtonFactory: (props?: HeaderMenuButton.Props) => {
        props.iconPath = props.hovered || props.menuOpen ? icons.active : icons.inactive;
        props.tabIndex = 0;
        props.ariaPopup = 'menu';
        props.altText = Messages.helpAltText();
        props.ariaLabel = Messages.helpAltText();
        props.title = Messages.helpTitle();

        return HeaderMenuButton.element(props);
      },

      menuPanelFactory: (helpMenuPanelProps?: HelpMenuPanel.Props) => {
        helpMenuPanelProps.itemSelected = this.itemSelected;

        // Copy properties from HelpMenu to HelpMenuPanel
        _.extend(helpMenuPanelProps, this.props);

        return React.createElement(HelpMenuPanel, helpMenuPanelProps);
      },
      onBeforeMenuOpen: () => {
        Telemetry.sendEvent<PageHeaderButtonClickEvent>({
          type: TelemetryEvents.PageHeaderButtonClick,
          button: 'help-menu',
          action: 'open'
        });
        window.addEventListener('blur', this.onWindowBlur);
        return Q.when();
      }
    });
  }

  private onWindowBlur = () => {
    this.itemSelected();
  }
}


export namespace HelpMenuAngularProvider {

  interface AngularServices {
    helpActions: HelpActions;
    serverService: ServerService;
  }

  function getAngularServicesWithServiceLocator() {
    const serverService = ServiceLocator.get<ServerService>('ServerService');

    return {
      helpActions: new HelpActions(serverService),
      serverService,
    };
  }

  function selectPropsFromAngularServices(angularResources: AngularServices): HelpMenuPropsFromAngular {
    const { helpActions, serverService } = angularResources;
    return {
      helpActions,
      serverHelpLabel: serverService.helpEdition() === 'online' ? Messages.onlineHelp() : Messages.serverHelp(),
      serverHelpUrl: HelpService.getServerHelpUrlByCategory(),
      supportUrl: HelpService.getGeneralSupportUrl(),
      whatIsNewUrl: HelpService.supportUrlPrefix() + '/support/releases/online',
      serverName: serverService.serverName(),
    };
  }

  export const HOC = angularProviderHOC<HelpMenuExternalProps & HelpMenuPropsFromRedux, HelpMenuPropsFromAngular, AngularServices>({
    getAngularServicesWithServiceLocator,
    selectPropsFromAngularServices,
  });
}


export namespace ReduxConnectedHelpMenu {

  const mapStateToProps: MapStateToProps<HelpMenuPropsFromRedux> = (state: AppState) => ({
    isOfflineHelpEnabled: SessionServerSelectors.offlineHelpEnabled(state),
    isSessionSiteOrServerAdmin: CompositeSessionInfoSelectors.currentUserIsSiteOrServerAdminSelector(state),
  });

  export const HOC = connect(mapStateToProps);
}


/*
 * Three levels of higher-order provider components, to gather props from three different sources:
 *   - Connected Redux component (props from Redux)
 *   - AngularProvider (props from Angular services)
 *   - Async store HOC (props from other API calls)
 */
export const HelpMenu: React.ComponentType<HelpMenuExternalProps> =
  ReduxConnectedHelpMenu.HOC(
    HelpMenuAngularProvider.HOC(
      HelpMenuViewComponent
    )
  );
